import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '../../../../components/common/icon/Icon';
import { IOpenMarketProduct } from '../../../../interfaces/Product.interfaces';
import { getShortAddress } from '../../../../utils/getter/getShortAddress.util';
import { ProductChip } from '../components/ProductChip';

export type IProductForSummaryCard = Pick<
  IOpenMarketProduct,
  'images' | 'machine' | 'manufacturedYear' | 'location' | 'operationTime'
>;

interface ProductSummaryCardProps {
  handleClick?: () => void;
  blurEffect: {
    isBlurred: boolean;
    dimmedText: string;
  };
  isNewProduct?: boolean;
  product: IProductForSummaryCard;
  price: string;
  title: string;
}

export const ProductSummaryCard = ({
  handleClick,
  blurEffect,
  isNewProduct = false,
  product,
  price,
  title,
}: ProductSummaryCardProps) => {
  const navigate = useNavigate();
  const { isBlurred, dimmedText } = blurEffect;

  return (
    <Container onClick={handleClick}>
      <ImageWrap>
        <Image src={product.images[0].imageUrl} isBlurred={isBlurred} />
        {isBlurred && <DimmedText>{dimmedText}</DimmedText>}
        {isNewProduct && (
          <BadgeContainer>
            <ProductChip type={'NEW'} />
          </BadgeContainer>
        )}
      </ImageWrap>

      <ProductInfoSection isBlurred={isBlurred}>
        <Brand>{product.machine?.brand?.name}</Brand>
        <Title>{title}</Title>
        <Price>{price}</Price>
        <LocationAndTimeContainer>
          <Location>
            <Icon name={'Marker'} width={12} height={12} />
            <LocationText>{getShortAddress(product.location)}</LocationText>
          </Location>
          <YearAndOprationTime>{`${String(product.manufacturedYear).slice(2, 4)}년식 · ${Number(
            product.operationTime,
          ).toLocaleString()}시간`}</YearAndOprationTime>
        </LocationAndTimeContainer>
      </ProductInfoSection>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 160px;

  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  align-self: stretch;

  gap: 16px;

  cursor: pointer;
`;

const ImageWrap = styled.div`
  min-width: 150px;
  height: 100%;

  position: relative;

  border-radius: 8px;

  background-color: #f2f3f5;

  padding: 0px 16px;
`;

const Image = styled.img<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  object-fit: cover;

  position: absolute;

  border-radius: 8px;

  opacity: ${(props) => (props.isBlurred ? 0.4 : 1)};
`;

const BadgeContainer = styled.div`
  position: absolute;

  top: 8px;
  left: 8px;

  z-index: 50;

  display: flex;
  gap: 4px;
`;

const DimmedText = styled.span`
  position: absolute;

  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 20.4px */

  mix-blend-mode: darken;

  opacity: 0.4;

  white-space: nowrap;
`;

const ProductInfoSection = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  gap: 8px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const LocationAndTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const Brand = styled.span`
  color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  align-self: stretch;

  overflow: hidden;
  text-overflow: ellipsis;
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  overflow: hidden;
`;

const Price = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const Location = styled.div`
  display: flex;
  padding: var(--radius-small, 4px) 6px;
  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  align-items: center;
  flex-shrink: 0;
  gap: 2px;
`;

const LocationText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const YearAndOprationTime = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  align-self: stretch;

  overflow: hidden;
  text-overflow: ellipsis;
`;
